import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  House, Receipt, Basket, ChartLineUp, ChatCircleDots, Plus,
  Gear, SignOut, ShieldCheck, Coins, Gift, Sparkle, CaretDown, UserPlus, SealCheck, EnvelopeSimple, ArrowsClockwise,
  DotsThreeOutline, FilePdf, Airplane, Storefront,
} from "@phosphor-icons/react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import api from "@/lib/api";
import { hasPendingScan } from "@/lib/pendingScan";
import { useAuth } from "@/context/AuthContext";
import AddReceiptModal from "@/components/AddReceiptModal";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home", icon: House },
  { to: "/receipts", label: "Receipts", icon: Receipt },
  { to: "/items", label: "Items", icon: Basket },
  { to: "/analytics", label: "Analytics", icon: ChartLineUp },
  { to: "/ask", label: "Ask AI", icon: ChatCircleDots },
];

const MORE_GROUPS = [
  {
    title: "Explore", items: [
      { to: "/items", label: "Items", icon: Basket },
      { to: "/ask", label: "Ask AI", icon: ChatCircleDots },
      { to: "/insights", label: "Insights", icon: Sparkle },
      { to: "/prices", label: "Where's it cheapest?", icon: Storefront },
    ],
  },
  {
    title: "Manage", items: [
      { to: "/warranties", label: "Warranties", icon: SealCheck },
      { to: "/subscriptions", label: "Subscriptions & Debit Orders", icon: ArrowsClockwise },
      { to: "/trips", label: "Trips", icon: Airplane },
      { to: "/reports", label: "Reports", icon: FilePdf },
    ],
  },
  {
    title: "Account", items: [
      { to: "/rewards", label: "Rewards", icon: Gift },
      { to: "/settings", label: "Settings", icon: Gear },
      { to: "/contact", label: "Contact us", icon: EnvelopeSimple },
    ],
  },
];

const SIDE_EXPLORE = [
  { to: "/insights", label: "Insights", icon: Sparkle, testid: "nav-insights" },
  { to: "/prices", label: "Where's it cheapest?", icon: Storefront, testid: "nav-prices" },
];
const SIDE_MANAGE = [
  { to: "/warranties", label: "Warranties", icon: SealCheck, testid: "nav-warranties" },
  { to: "/subscriptions", label: "Subscriptions & Debit Orders", icon: ArrowsClockwise, testid: "nav-subscriptions" },
  { to: "/trips", label: "Trips", icon: Airplane, testid: "nav-trips" },
  { to: "/reports", label: "Reports", icon: FilePdf, testid: "nav-reports" },
];

function SideGroupLabel({ children }) {
  return (
    <div data-testid={`side-group-${String(children).toLowerCase()}`} className="mt-4 mb-1 px-4 text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400">
      {children}
    </div>
  );
}

function SideLink({ to, label, icon: Icon, testid, badge = 0 }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-200 ${
          isActive ? "bg-riq-sage/70 text-riq-primary" : "text-neutral-500 hover:bg-secondary"
        }`
      }
    >
      <Icon size={22} weight="duotone" /> {label}
      {badge > 0 && (
        <span data-testid="nav-warranties-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-riq-gold text-white text-[11px] font-bold flex items-center justify-center">{badge}</span>
      )}
    </NavLink>
  );
}


const AVATAR_COLORS = ["#6E3FFC", "#42C97C", "#F5A623", "#FF6B6B", "#2BB0C4", "#B36EFF", "#FF9F1C", "#E0509E"];

function initialsOf(name, email) {
  const src = (name || "").trim() || (email || "").split("@")[0] || "?";
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : src.slice(0, 2);
  return letters.toUpperCase();
}

function colorFor(seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function InitialsAvatar({ name, email, picture, size = 32, testId }) {
  if (picture) {
    return <img src={picture} alt={name || email} data-testid={testId} style={{ width: size, height: size }} className="rounded-full object-cover" />;
  }
  return (
    <div
      data-testid={testId}
      style={{ width: size, height: size, background: colorFor(email || name), fontSize: size * 0.4 }}
      className="rounded-full flex items-center justify-center font-bold text-white shrink-0"
    >
      {initialsOf(name, email)}
    </div>
  );
}

function Brand({ variant = "desktop" }) {
  const { user, logout, profiles, switchProfile } = useAuth();
  const navigate = useNavigate();
  const others = (profiles || []).filter((p) => p.user_id !== user?.user_id);
  const triggerTestId = variant === "mobile" ? "brand-menu-trigger" : "brand-menu-trigger-desktop";

  const doLogout = () => { logout(); navigate("/login"); };
  const addAccount = () => navigate("/login?add=1");
  const doSwitch = async (p) => {
    try {
      toast.loading(`Switching to ${p.name || p.email}…`, { id: "switch" });
      await switchProfile(p);
    } catch {
      toast.error("That session expired — please sign in again.", { id: "switch" });
      navigate("/login");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={triggerTestId} className="flex items-center gap-2.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-riq-primary/40">
          <div className="w-9 h-9 rounded-xl bg-riq-primary flex items-center justify-center p-2">
            <img src="/sliply-mark.png" alt="Sliply" className="w-full h-full object-contain" />
          </div>
          <div className="font-head text-lg font-bold tracking-tight text-riq-primary">Sliply</div>
          {user && <InitialsAvatar name={user.name} email={user.email} picture={user.picture} size={26} testId="brand-avatar" />}
          <CaretDown size={14} weight="bold" className="text-neutral-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64" data-testid="brand-menu">
        <div className="flex items-center gap-3 px-2 py-2">
          <InitialsAvatar name={user?.name} email={user?.email} picture={user?.picture} size={38} />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-riq-primary truncate">{user?.name || "Signed in"}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <DropdownMenuSeparator />

        {others.length > 0 && (
          <>
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">Switch profile</DropdownMenuLabel>
            {others.map((p) => (
              <DropdownMenuItem key={p.user_id} data-testid={`switch-profile-${p.user_id}`} onClick={() => doSwitch(p)} className="cursor-pointer gap-2">
                <InitialsAvatar name={p.name} email={p.email} picture={p.picture} size={26} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{p.name || p.email.split("@")[0]}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{p.email}</div>
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem data-testid="menu-add-account" onClick={addAccount} className="cursor-pointer">
          <UserPlus size={16} weight="duotone" className="mr-2" /> Add another account
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-warranties" onClick={() => navigate("/warranties")} className="cursor-pointer">
          <SealCheck size={16} weight="duotone" className="mr-2" /> Warranties
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-subscriptions" onClick={() => navigate("/subscriptions")} className="cursor-pointer">
          <ArrowsClockwise size={16} weight="duotone" className="mr-2" /> Subscriptions &amp; Debit Orders
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")} className="cursor-pointer">
          <Gear size={16} weight="duotone" className="mr-2" /> Settings
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-contact" onClick={() => navigate("/contact")} className="cursor-pointer">
          <EnvelopeSimple size={16} weight="duotone" className="mr-2" /> Contact us
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={doLogout} className="cursor-pointer text-riq-accent focus:text-riq-accent">
          <SignOut size={16} weight="duotone" className="mr-2" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [addOpen, setAddOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [warrantyAlert, setWarrantyAlert] = useState(0);

  useEffect(() => {
    if (!user) return;
    api.get("/warranties").then((r) => setWarrantyAlert(r.data?.counts?.expiring_soon || 0)).catch(() => {});
  }, [user?.user_id, location.pathname]);

  // Resume an interrupted receipt scan after an app reload.
  useEffect(() => {
    if (user && hasPendingScan()) setAddOpen(true);
  }, [user?.user_id]);

  const onSaved = (receipt) => {
    setAddOpen(false);
    if (receipt?.id) navigate(`/receipts/${receipt.id}`);
  };

  return (
    <div className="min-h-screen bg-riq-bg flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-riq-border bg-riq-bg fixed h-screen p-6">
        <Brand variant="desktop" />
        <button
          data-testid="add-receipt-sidebar-btn"
          onClick={() => setAddOpen(true)}
          className="mt-8 flex items-center justify-center gap-2 rounded-full bg-riq-primary text-white py-3 font-medium transition-transform duration-200 hover:-translate-y-0.5"
        >
          <Plus size={18} weight="bold" /> Add Receipt
        </button>
        <NavLink
          to="/rewards"
          data-testid="nav-rewards-balance"
          className="mt-3 flex items-center justify-between rounded-2xl border border-riq-border bg-white px-4 py-3 hover:-translate-y-0.5 transition-transform"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-riq-primary"><Coins size={20} weight="fill" className="text-riq-gold" /> Moolas</span>
          <span data-testid="sidebar-moolas" className="font-head font-bold text-riq-primary">{(user?.moolas ?? 0).toLocaleString()}</span>
        </NavLink>
        <nav className="mt-8 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-riq-sage/70 text-riq-primary" : "text-neutral-500 hover:bg-secondary"
                }`
              }
            >
              <n.icon size={22} weight="duotone" /> {n.label}
            </NavLink>
          ))}

          <SideGroupLabel>Explore</SideGroupLabel>
          {SIDE_EXPLORE.map((n) => <SideLink key={n.to} {...n} />)}

          <SideGroupLabel>Manage</SideGroupLabel>
          {SIDE_MANAGE.map((n) => (
            <SideLink key={n.to} {...n} badge={n.to === "/warranties" ? warrantyAlert : 0} />
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-1">
          <SideGroupLabel>Account</SideGroupLabel>
          <NavLink to="/rewards" data-testid="nav-rewards" className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm ${isActive ? "bg-riq-sage/70 text-riq-primary" : "text-neutral-500 hover:bg-secondary"}`}>
            <Gift size={20} weight="duotone" /> Rewards
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm ${isActive ? "bg-riq-sage/70 text-riq-primary" : "text-neutral-500 hover:bg-secondary"}`}>
              <ShieldCheck size={20} weight="duotone" /> Admin
            </NavLink>
          )}
          <NavLink to="/settings" data-testid="nav-settings" className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm ${isActive ? "bg-riq-sage/70 text-riq-primary" : "text-neutral-500 hover:bg-secondary"}`}>
            <Gear size={20} weight="duotone" /> Settings
          </NavLink>
          <NavLink to="/contact" data-testid="nav-contact" className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm ${isActive ? "bg-riq-sage/70 text-riq-primary" : "text-neutral-500 hover:bg-secondary"}`}>
            <EnvelopeSimple size={20} weight="duotone" /> Contact us
          </NavLink>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-neutral-500 hover:bg-secondary text-left">
            <SignOut size={20} weight="duotone" /> Log out
          </button>
          <div className="mt-3 px-1 text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 max-w-full lg:ml-64 pb-nav-safe lg:pb-8">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-20 flex items-center justify-between px-5 pb-4 pt-4 safe-pt bg-riq-bg/90 backdrop-blur-xl border-b border-riq-border">
          <Brand variant="mobile" />
          <div className="flex items-center gap-3">
            <button data-testid="mobile-moolas" onClick={() => navigate("/rewards")} className="flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-3 py-1.5 text-sm font-semibold">
              <Coins size={16} weight="fill" className="text-riq-gold" /> {(user?.moolas ?? 0).toLocaleString()}
            </button>
            {user?.role === "admin" && (
              <button onClick={() => navigate("/admin")} className="text-neutral-500"><ShieldCheck size={22} weight="duotone" /></button>
            )}
          </div>
        </div>

        <main className="max-w-6xl mx-auto px-5 sm:px-8 py-6 sm:py-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              <Outlet context={{ openAddReceipt: () => setAddOpen(true) }} />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Bottom nav (mobile) */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 rounded-t-3xl border-t border-riq-border bg-white/95 backdrop-blur-xl px-2 pt-2 pb-3 safe-pb flex justify-around items-center">
        {NAV.slice(0, 2).map((n) => <BottomItem key={n.to} {...n} />)}
        <button
          data-testid="add-receipt-fab"
          onClick={() => setAddOpen(true)}
          className="-mt-8 w-14 h-14 rounded-full bg-riq-primary text-white flex items-center justify-center shadow-lg shadow-riq-primary/30 active:scale-95 transition-transform shrink-0"
        >
          <Plus size={26} weight="bold" />
        </button>
        <BottomItem {...NAV[3]} />
        <button
          data-testid="bottomnav-more"
          onClick={() => setMoreOpen(true)}
          className="flex flex-col items-center gap-1 px-1 py-1 text-[10px] font-medium text-neutral-400 min-w-0"
        >
          <DotsThreeOutline size={22} weight="duotone" />
          More
        </button>
      </nav>

      {/* More sheet (mobile) */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" data-testid="more-sheet" className="rounded-t-3xl border-riq-border p-0 max-h-[85vh] overflow-y-auto">
          <div className="mx-auto mt-3 h-1.5 w-10 rounded-full bg-neutral-200" />
          <div className="px-5 pt-4 pb-1">
            <h3 className="font-head text-lg font-bold text-riq-primary">More</h3>
          </div>
          <div className="px-4 pb-8 safe-pb space-y-5">
            {MORE_GROUPS.map((group) => {
              const items = group.title === "Account" && user?.role === "admin"
                ? [...group.items, { to: "/admin", label: "Admin", icon: ShieldCheck }]
                : group.items;
              return (
                <div key={group.title} data-testid={`more-group-${group.title.toLowerCase()}`}>
                  <div className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-400">{group.title}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {items.map((n) => (
                      <button
                        key={n.to}
                        data-testid={`more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => { setMoreOpen(false); navigate(n.to); }}
                        className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-riq-border bg-white py-4 text-neutral-600 active:scale-95 transition-transform"
                      >
                        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-riq-sage/60 text-riq-primary">
                          <n.icon size={22} weight="duotone" />
                        </span>
                        <span className="text-xs font-medium text-center leading-tight">{n.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      <AddReceiptModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={onSaved} />
    </div>
  );
}

function BottomItem({ to, label, icon: Icon }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={`bottomnav-${label.toLowerCase().replace(" ", "-")}`}
      className={({ isActive }) =>
        `flex flex-col items-center gap-1 px-2 py-1 text-[10px] font-medium ${isActive ? "text-riq-primary" : "text-neutral-400"}`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={22} weight={isActive ? "fill" : "duotone"} />
          {label}
        </>
      )}
    </NavLink>
  );
}
